import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Nullable } from '@src/common-utils/Models';
import { AuthInfo, AuthStatus, LoginData, SignUpRequest, UserInfo } from '@src/common-utils/AuthModels';
// import  {
// signInUser,
// signOutUser,
// forgotPassword,
// confirmResetPassword,
// sendConfirmationCode} from '@src/libs/cognito-client';
import {
  signInUser,
  signUpUser,
  signOutUser,
  sendConfirmationCode,
  forgotPassword,
  confirmResetPassword,
} from '@src/queries/AuthQuery';
import {
  checkAuthenticated,
  clearLocalStore,
  getStoreAuthInfo,
  storeAuthInfo,
  touchLastAccess,
  TOKEN_EXPIRATION_MARGIN_SECONDS,
} from '@src/common-utils/StoreUtil';
// import { setRefreshTokenInCookie } from '@src/libs/refresh-token';

type ActionCallback = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

type LoginProps = {
  loginData: LoginData;
} & ActionCallback;

type SignupProps = {
  signupData: SignUpRequest;
} & ActionCallback;

type SendResetPasswordCodeProps = {
  email: string;
  recaptchaToken: string;
} & ActionCallback;

type ResetPasswordProps = {
  email: string;
  newPassword: string;
  confirmationCode: string;
} & ActionCallback;

type ResendConfirmationCodeProps = {
  email: string;
  recaptchaToken: string;
} & ActionCallback;

type UserContextType = {
  isLoading?: boolean;
  userInfo: Nullable<UserInfo>;
  login: (props: LoginProps) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (props: SignupProps) => Promise<void>;
  sendResetPasswordCode: (props: SendResetPasswordCodeProps) => Promise<void>;
  resetPassword: (props: ResetPasswordProps) => Promise<void>;
  resendConfirmationCode: (props: ResendConfirmationCodeProps) => Promise<void>;
  getAuthStatus: () => AuthStatus;
  refreshUserInfo: () => void;
};

export const UserContext = createContext<UserContextType>({
  isLoading: false,
  userInfo: null,
  login: async () => {
    //no op
  },
  logout: async () => {
    // no op
  },
  signUp: async () => {
    //no op
  },
  sendResetPasswordCode: async () => {
    //no op
  },
  resetPassword: async () => {
    //no op
  },
  resendConfirmationCode: async () => {
    //no op
  },
  getAuthStatus: () => AuthStatus.EXPIRED,

  refreshUserInfo: async () => {
    //no op
  },
});

// const doLogin = async ({ email, password }: LoginData): Promise<AuthInfo> => {
//   try {
//     const { authInfo, refreshToken } = await signInUser(email, password);
//     storeAuthInfo(authInfo);
//     await setRefreshTokenInCookie(refreshToken);

//     return authInfo;
//   } catch (e) {
//     clearLocalStore();
//     throw e;
//   }
// };

const doLogout = async () => {
  const authInfo = getStoreAuthInfo();
  if (!authInfo) {
    return;
  }

  try {
    await signOutUser(authInfo.accessToken);
  } catch (e) {
    // can ignore logout error
  }

  clearLocalStore();
};

// The stored user, unless the session is over. An expired token is still restorable while the user is within the idle
// window: the axios interceptor refreshes it on the next secured request.
const getRestorableUserInfo = (): Nullable<UserInfo> =>
  checkAuthenticated() === AuthStatus.EXPIRED ? null : getStoreAuthInfo()?.userInfo ?? null;

const buildError = (e: unknown) => (e instanceof Error ? e : Error('Unexpected error'));

// How often to re-check the session while the tab stays open (see the effect below). Matches
// TOKEN_EXPIRATION_MARGIN_SECONDS so the poll granularity is no coarser than the margin the rest of the auth code
// already relies on.
const SESSION_CHECK_INTERVAL_MS = TOKEN_EXPIRATION_MARGIN_SECONDS * 1000;

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);
  // Restore the session synchronously (not in an effect): child effects run before this provider's effects, so a
  // protected page (withAuth) would otherwise see a null user on browser refresh and redirect to sign-in.
  const [userInfo, setUserInfo] = useState<Nullable<UserInfo>>(getRestorableUserInfo);

  useEffect(() => {
    if (checkAuthenticated() === AuthStatus.EXPIRED) {
      clearLocalStore();
    } else {
      touchLastAccess();
    }
  }, []);

  // Nothing else re-checks the session while the tab stays open: withAuth only validates when a protected page
  // freshly mounts, and axios only reacts when a secured request actually happens to be made. A user who idles past
  // the idle window on an already-open page (protected or not, e.g. Home) would otherwise stay "logged in" in the
  // header indefinitely. Poll instead of a single timeout so a system sleep/wake doesn't leave a stale timer.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (userInfo && checkAuthenticated() === AuthStatus.EXPIRED) {
        clearLocalStore();
        setUserInfo(null);
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [userInfo]);

  const login = async ({ loginData, onSuccess, onError }: LoginProps) => {
    setIsLoading(true);
    try {
      const authInfo: AuthInfo = await signInUser(loginData.email, loginData.password);
      storeAuthInfo(authInfo);
      touchLastAccess();
      setUserInfo(authInfo.userInfo);
      setIsLoading(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setIsLoading(false);
      setUserInfo(null);
      clearLocalStore();
      if (onError) {
        console.log(e);
        onError(buildError(e));
      }
    }
  };

  const logout = async () => {
    await doLogout();
    setUserInfo(null);
  };

  const signUp = async ({ signupData, onSuccess, onError }: SignupProps) => {
    setIsLoading(true);
    try {
      await signUpUser(signupData);
      setIsLoading(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setIsLoading(false);
      if (onError) {
        onError(buildError(e));
      }
    }
  };

  const sendResetPasswordCode = async ({ email, recaptchaToken, onSuccess, onError }: SendResetPasswordCodeProps) => {
    setIsLoading(true);
    try {
      await forgotPassword(email, recaptchaToken);
      setIsLoading(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setIsLoading(false);
      if (onError) {
        onError(buildError(e));
      }
    }
  };

  const resetPassword = async ({ email, newPassword, confirmationCode, onSuccess, onError }: ResetPasswordProps) => {
    setIsLoading(true);
    try {
      await confirmResetPassword({ email, newPassword, confirmationCode });
      setIsLoading(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setIsLoading(false);
      if (onError) {
        onError(buildError(e));
      }
    }
  };

  const resendConfirmationCode = async ({ email, recaptchaToken, onSuccess, onError }: ResendConfirmationCodeProps) => {
    setIsLoading(true);
    try {
      await sendConfirmationCode(email, recaptchaToken);
      setIsLoading(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      setIsLoading(false);
      if (onError) {
        onError(buildError(e));
      }
    }
  };

  const refreshUserInfo = async () => {
    const authInfo = getStoreAuthInfo();
    setUserInfo(authInfo?.userInfo ? authInfo.userInfo : null);
  };

  const getAuthStatus = () => checkAuthenticated();

  return (
    <UserContext.Provider
      value={{
        isLoading,
        userInfo,
        login,
        logout,
        signUp,
        sendResetPasswordCode,
        resetPassword,
        resendConfirmationCode,
        getAuthStatus,
        refreshUserInfo,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
